import { getCartErrorMessage } from '@theme/cart-feedback';
import { updateThemeCart } from '@theme/theme-cart';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    try {
      // Checked add-on toggles (e.g. Custom Message under Greeting Card) ride along as their own lines.
      const addonToggles = this.#checkedAddons();
      const addonLines = addonToggles.map((toggle) => ({ merchandiseId: toggle.dataset.addonVariant, quantity: 1 }));
      const result = await updateThemeCart({ lines: [line, ...addonLines] }, { signal, silentErrors: true });
      if (!this.#resultErrorMessage(result)) {
        addonToggles.forEach((toggle) => toggle.setAttribute('aria-checked', 'false'));
      }

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
      } else if (this.hasAttribute('data-quiet-add') && !addonLines.length) {
        // Single spices (merchant, Oct 26 2026): stay on the page; a short confirmation instead of opening the cart.
        showAddedToast(submitter);
      } else {
        const drawer = document.getElementById('cart-drawer');
        if (drawer instanceof HTMLDialogElement) {
          if (!drawer.open) drawer.showModal();
          drawer.dispatchEvent(new CustomEvent('s-cart:opened', { bubbles: true, detail: { dialogId: drawer.id } }));
        } else {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        }
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  #checkedAddons() {
    const group = this.dataset.addonGroup;
    if (!group) return [];
    return [...document.querySelectorAll('button[data-addon-variant][aria-checked="true"]')]
      .filter((toggle) => toggle.dataset.addonGroup === group);
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

// Checkout guard: uneven Greeting Cards / Custom Messages only surface when the shopper tries to check out.
const giftMatchFlag = 'giftMatchAttempted';
const setGiftMatchAttempted = (on) => {
  document.documentElement.classList.toggle('gift-match-attempted', on);
  try {
    if (on) sessionStorage.setItem(giftMatchFlag, '1');
    else sessionStorage.removeItem(giftMatchFlag);
  } catch (error) {
    // Storage can be unavailable (private mode); the class still works for this page view.
  }
};

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (event.submitter?.getAttribute('name') !== 'checkout') return;

  if (!form.querySelector('[data-gift-blocked]')) {
    setGiftMatchAttempted(false);
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  setGiftMatchAttempted(true);
  const scope = form.closest('dialog') || document;
  const notice = scope.querySelector('[data-gift-match]') || document.querySelector('[data-gift-match]');
  notice?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}, true);

// Proceed Anyway goes to checkout; clear the reminder so it doesn't linger.
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-gift-match] a[href$="/checkout"]') : null;
  if (link) setGiftMatchAttempted(false);
});

// Simple +/- steppers (cart Greeting Card / Custom Message fixer): [data-qty-step] inside [data-qty-group].
document.addEventListener('click', (event) => {
  const stepButton = event.target instanceof Element ? event.target.closest('[data-qty-step]') : null;
  if (!stepButton) return;
  const input = stepButton.closest('[data-qty-group]')?.querySelector('input[name="quantity"]');
  if (!input) return;
  const current = Number.parseInt(input.value, 10) || 1;
  input.value = String(Math.max(1, current + Number(stepButton.dataset.qtyStep)));
});

// Add-on checkboxes only toggle; the linked add button does the adding.
document.addEventListener('click', (event) => {
  const toggle = event.target instanceof Element ? event.target.closest('button[data-addon-variant]') : null;
  if (!toggle || toggle.getAttribute('aria-disabled') === 'true') return;
  toggle.setAttribute('aria-checked', toggle.getAttribute('aria-checked') === 'true' ? 'false' : 'true');
});

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}

// Fallback fill (Oct 26 2026): when a page runs past its per-handle product lookup limit, quick-add renders its
// controls disabled with data-qa-fill="<handle>" (blocks/quick-add.liquid). Fill in the first available variant from
// /products/<handle>.js and enable them; prices tagged data-qa-price="<card>[,<message>]" get real prices too.
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cache = {};
  const load = (h) => (cache[h] ||= fetch(`${root}products/${encodeURIComponent(h)}.js`, { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null)).catch(() => null));
  const money = (cents, fmt) => {
    const amount = (cents / 100).toFixed(2);
    return (fmt || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
  };
  const fill = async () => {
    for (const el of document.querySelectorAll('[data-qa-fill]:not([data-qa-done])')) {
      el.setAttribute('data-qa-done', '');
      const p = await load(el.dataset.qaFill);
      const v = p && (p.variants || []).find((x) => x.available);
      if (!v) continue;
      if (el.matches('input[name="id"]')) {
        el.value = v.id;
        const btn = el.form && el.form.querySelector('button[name="add"]');
        if (btn) btn.disabled = false;
      } else {
        el.dataset.addonVariant = String(v.id);
        el.disabled = false;
      }
    }
    for (const el of document.querySelectorAll('[data-qa-price]:not([data-qa-done])')) {
      el.setAttribute('data-qa-done', '');
      const [cardH, msgH] = el.dataset.qaPrice.split(',');
      const card = await load(cardH);
      const msg = msgH ? await load(msgH) : null;
      if (!card) continue;
      const base = card.price;
      const fmt = el.dataset.qaFmt;
      el.dataset.base = money(base, fmt);
      if (msg) el.dataset.with = money(base + msg.price, fmt);
      const on = el.dataset.addonPrice && document.querySelector(`button[data-addon-group="${CSS.escape(el.dataset.addonPrice)}"][aria-checked="true"]`);
      el.textContent = on && el.dataset.with ? el.dataset.with : el.dataset.base;
    }
    for (const el of document.querySelectorAll('[data-qa-msg-price]:not([data-qa-done])')) {
      el.setAttribute('data-qa-done', '');
      const m = await load(el.dataset.qaMsgPrice);
      if (m) el.textContent = money(m.price, el.dataset.qaFmt);
    }
  };
  let queued = false;
  const schedule = () => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; fill(); }); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule); else schedule();
  new MutationObserver((records) => {
    for (const r of records) for (const n of r.addedNodes) {
      if (n instanceof Element && (n.matches('[data-qa-fill],[data-qa-price],[data-qa-msg-price]') || n.querySelector('[data-qa-fill],[data-qa-price],[data-qa-msg-price]'))) { schedule(); return; }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

// Quiet add confirmation for single spices (merchant, Oct 26 2026): the button reads "✓ Added" for a moment and a small
// note with View Cart slides in, then fades on its own. Sets, builds and gift items still open the cart.
let addedToastTimer = 0;
function showAddedToast(submitter) {
  const t = window.WZ_ADDED || {};
  if (submitter instanceof HTMLElement) {
    // Swap the whole button content (keeps icons intact on restore) at the same width.
    if (submitter.dataset.qaSaved == null) {
      submitter.dataset.qaSaved = submitter.innerHTML;
      submitter.style.minWidth = `${submitter.offsetWidth}px`;
    }
    submitter.textContent = t.added || '✓ Added';
    clearTimeout(Number(submitter.dataset.qaTimer || 0));
    submitter.dataset.qaTimer = String(setTimeout(() => {
      submitter.innerHTML = submitter.dataset.qaSaved;
      submitter.style.minWidth = '';
      delete submitter.dataset.qaSaved;
    }, 1600));
  }
  let toast = document.getElementById('wz-added-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'wz-added-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = '<span class="wz-at-text"></span><button type="button" class="wz-at-view"></button>';
    const style = document.createElement('style');
    style.textContent = '#wz-added-toast{position:fixed;z-index:60;right:24px;top:96px;display:flex;align-items:center;gap:14px;padding:12px 14px 12px 18px;background:#0E4A32;color:#fff;border-radius:999px;box-shadow:0 8px 24px rgba(0,0,0,.18);font-size:.95rem;opacity:0;transform:translateY(-8px);pointer-events:none;transition:opacity .2s,transform .2s}'
      + '#wz-added-toast.is-on{opacity:1;transform:none;pointer-events:auto}'
      + '#wz-added-toast .wz-at-view{background:#fff;color:#0E4A32;border:0;border-radius:999px;padding:6px 14px;font-weight:600;font-size:.8rem;letter-spacing:.06em;text-transform:uppercase;cursor:pointer}'
      + '@media (max-width:767px){#wz-added-toast{left:16px;right:16px;top:auto;bottom:16px;justify-content:space-between;transform:translateY(8px)}}'
      + '@media (prefers-reduced-motion:reduce){#wz-added-toast{transition:none}}';
    document.head.appendChild(style);
    document.body.appendChild(toast);
    toast.querySelector('.wz-at-view').addEventListener('click', () => {
      toast.classList.remove('is-on');
      const drawer = document.getElementById('cart-drawer');
      if (drawer instanceof HTMLDialogElement) {
        if (!drawer.open) drawer.showModal();
        drawer.dispatchEvent(new CustomEvent('s-cart:opened', { bubbles: true, detail: { dialogId: drawer.id } }));
      } else {
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      }
    });
    toast.addEventListener('mouseenter', () => clearTimeout(addedToastTimer));
    toast.addEventListener('mouseleave', () => { addedToastTimer = setTimeout(() => toast.classList.remove('is-on'), 1500); });
  }
  toast.querySelector('.wz-at-text').textContent = t.toast || '✓ Added to your cart';
  toast.querySelector('.wz-at-view').textContent = t.view || 'View Cart';
  toast.classList.add('is-on');
  clearTimeout(addedToastTimer);
  addedToastTimer = setTimeout(() => toast.classList.remove('is-on'), 3500);
}
